import { useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Input } from '../../../../../components/ui/Input';
import { Link } from '../../../../../components/ui/Link';
import { Popconfirm } from '../../../../../components/ui/Popconfirm';
import { Select } from '../../../../../components/ui/Select';
import { Table, type TableColumn } from '../../../../../components/ui/Table';
import { Tag } from '../../../../../components/ui/Tag';
import { Tooltip } from '../../../../../components/ui/Tooltip';
import { Tour, type TourStep } from '../../../../../components/ui/Tour';
import type { Coupon, CouponGroup } from '../fixtures';
import { buildUnifiedRules, type UnifiedGroupRule, type UnifiedRule } from '../stackRules';
import { ruleKindTagCategory } from './couponPresentation';
import styles from './CouponManagementPage.module.css';

interface RulesViewProps {
  entities: readonly Coupon[];
  groups: readonly CouponGroup[];
  /** 初始状态演示：列表只有一条历史叠加组合与引导气泡；删掉后即空态提示。 */
  initialMode?: boolean;
  onAddRule: () => void;
  onDeleteRule: (rule: UnifiedRule) => void;
  onEditRule: (key: string) => void;
  onOpenExample: () => void;
  onShowNotice?: (message: string) => void;
}

/** 初始状态下唯一的一条历史规则：老版本「允许叠加」的券已统一收进默认分组。 */
const historyRule: UnifiedGroupRule = {
  group: { id: 0, name: '历史叠加组合', couponIds: [] },
  key: 'group-history',
  kind: 'group',
  name: '历史叠加组合',
  range: '暑期续费赠课时、数学报名优惠券、数学报名优惠券、数学报名优惠券',
  status: '',
};

/** 初始状态只有一条历史组合，引导就框住那一行（锚点由 Table 的 rowProps 打到 tr 上）。文案与 Figma 气泡（12471-25827）一致；气泡在行上方、指针垂下指向行。 */
const historyTourSteps: readonly TourStep[] = [{
  align: 'start',
  content: '原来开启“允许叠加”的优惠券，已统一加入「默认分组」。你可以点击“编辑”，调整组内优惠券。',
  side: 'top',
  target: '[data-tour="rules-history-row"]',
}];

function ruleTypeText(rule: UnifiedRule): string {
  if (rule.kind === 'group') return '组合叠加';
  return `单券叠加·${rule.entity.stackMode === 'global' ? '全局' : '指定'}`;
}

function ruleSearchText(rule: UnifiedRule, entities: readonly Coupon[]): string {
  const rangeNames = rule.kind === 'single' && rule.entity.stackMode === 'global'
    ? entities.filter((coupon) => coupon.kind === 'ordinary').map((coupon) => coupon.name)
    : [rule.range];
  return [rule.name, ...rangeNames].join(' ');
}

function compactRangeText(range: string): string {
  const names = range.split('、');
  if (names.length <= 3) return range;
  return `${names.slice(0, 3).join('、')}、+${names.length - 3}`;
}

export function RulesView({ entities, groups, initialMode = false, onAddRule, onDeleteRule, onEditRule, onOpenExample, onShowNotice }: RulesViewProps) {
  /* 初始状态里那条历史组合可以真删：删掉后列表为空，正好展示「暂无叠加规则」的空态提示。 */
  const [historyDeleted, setHistoryDeleted] = useState(false);
  const rules = initialMode ? (historyDeleted ? [] : [historyRule]) : buildUnifiedRules(entities, groups);
  const [guideDismissed, setGuideDismissed] = useState(false);
  const [queryDraft, setQueryDraft] = useState('');
  const [kindDraft, setKindDraft] = useState('');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const normalizedQuery = query.trim();
  const filteredRules = rules.filter((rule) => (!normalizedQuery || ruleSearchText(rule, entities).includes(normalizedQuery)) && (!kind || rule.kind === kind));

  function queryRules() {
    setQuery(queryDraft);
    setKind(kindDraft);
    setPage(1);
  }

  function resetFilters() {
    setQueryDraft('');
    setKindDraft('');
    setQuery('');
    setKind('');
    setPage(1);
  }

  function showStaticNotice() {
    onShowNotice?.('初始状态数据仅供展示，开启模拟数据后可编辑');
  }

  function deleteHistoryRule() {
    setHistoryDeleted(true);
    onShowNotice?.('叠加规则已删除');
  }

  const columns: TableColumn<UnifiedRule>[] = [
    { fixed: 'left', key: 'name', render: (rule) => rule.name, title: '名称 / 主券', width: 220 },
    { key: 'kind', render: (rule) => <Tag category={ruleKindTagCategory(rule.kind)}>{ruleTypeText(rule)}</Tag>, title: '规则类型', width: 150 },
    {
      key: 'range',
      render: (rule) => rule.range
        ? <Tooltip content={rule.range}><span className={styles.ruleRange} tabIndex={0}>{compactRangeText(rule.range)}</span></Tooltip>
        : '-',
      title: '叠加范围',
      width: 460,
    },
    {
      key: 'actions',
      render: (rule) => initialMode
        ? <span className={styles.ruleActions}><Link onClick={showStaticNotice}>编辑</Link><Popconfirm confirmText="删除" confirmType="danger" description="删除后，列表将没有任何叠加规则。" onConfirm={deleteHistoryRule} title={`确定删除“${rule.name}”吗？`}><Link type="danger">删除</Link></Popconfirm></span>
        : <span className={styles.ruleActions}><Link onClick={() => onEditRule(rule.key)}>编辑</Link><Popconfirm confirmText="删除" confirmType="danger" description="删除后，由该规则产生的叠加关系将失效；其他规则产生的关系不受影响。" onConfirm={() => onDeleteRule(rule)} title={`确定删除“${rule.name}”的叠加规则吗？`}><Link type="danger">删除</Link></Popconfirm></span>,
      title: '操作',
      fixed: 'right',
      width: 140,
    },
  ];

  return <>
    <div className={styles.filters}>
      <Input ariaLabel="搜索优惠券名称或组合名称" className={styles.filterInput} onValueChange={setQueryDraft} placeholder="搜索优惠券名称或组合名称" value={queryDraft} />
      <label>规则类型<Select ariaLabel="规则类型筛选" className={styles.filterSelect} onValueChange={setKindDraft} options={[{ label: '不限', value: '' }, { label: '单券规则', value: 'single' }, { label: '组合规则', value: 'group' }]} value={kindDraft} /></label>
      <div className={styles.filterActions}><Button onClick={queryRules}>查询</Button><Button onClick={resetFilters} type="default">重置</Button></div>
    </div>
    <div className={styles.rulesToolbar}>
      <Button onClick={onAddRule}>新增规则</Button>
      <Button type="default">导出</Button>
    </div>
    <div>
      {initialMode ? <Tour active={!guideDismissed} finishText="知道了" onFinish={() => setGuideDismissed(true)} steps={historyTourSteps} /> : null}
      <div className={styles.rulesIntro}>
        <p className={styles.rulesIntroText}>单券规则和组合规则统一管理。多张优惠券同时使用时，任意两张券之间都必须存在叠加关系，叠加关系不会自动传递。</p>
        <Link onClick={onOpenExample}>查看示例</Link>
      </div>
      <Table ariaLabel="叠加规则" columns={columns} emptyText="暂无叠加规则" pagination={{ ariaLabel: '叠加规则分页', footerStart: <span>共 {filteredRules.length} 条</span>, onPageChange: setPage, page, pageSize: 10 }} rowKey={(rule) => rule.key} rowProps={initialMode ? () => ({ 'data-tour': 'rules-history-row' }) : undefined} rows={filteredRules} />
    </div>
  </>;
}
